import React, { useState, useEffect } from "react";
import * as bootstrap from "bootstrap";
import { AppSettings } from "../../config/app-settings.js";
import useAuthStore from "../../stores/auth-store.js";
import useModelMappingStore from "../../stores/model-mapping-store.js";
import {
  useAvailableLayers,
  usePendingFeatures,
} from "../../hooks/useModelMapping.js";
import {
  pointStyles,
  lineStyles,
  areaStyles,
} from "../map/utils/map-styles.js";
import DataTable from "react-data-table-component";
import { Modal, Button } from "react-bootstrap";
import { map } from "leaflet";

const ModelMappingSelector = () => {
  // ==================== LOCAL VARIABLES ====================
  // List of all selectable layers (checkboxes)
  const selectors = [
    "CRD_Transit_Stops_v1",
    "CRD_Grade_Schools_v1",
    "CRD_PostSecondary_Schools_v1",
    "CRD_Model_Road_Network_v1",
    "CRD_Transit_Routes_v1",
    "CRD_Municipal_Boundaries_v1",
    "CRD_TAZ_2025_v2",
  ];
  // Layer mappings definition
  const layerCategories = {
    points: {
      title: "Points Layers",
      layers: {
        CRD_Transit_Stops_v1: {
          label: "CRD Transit Stops v1",
          description: "Show/hide CRD transit stops on the map",
          fileName: "CRD_Transit_Stops_v1.geojson",
          mandatory: false,
        },
        CRD_Grade_Schools_v1: {
          label: "CRD Grade Schools v1",
          description: "Show/hide CRD grade schools on the map",
          fileName: "CRD_Grade_Schools_v1.geojson",
          mandatory: false,
        },
        CRD_PostSecondary_Schools_v1: {
          label: "CRD Post-Secondary Schools v1",
          description: "Show/hide CRD post-secondary schools on the map",
          fileName: "CRD_PostSecondary_Schools_v1.geojson",
          mandatory: false,
        },
      },
    },
    network: {
      title: "Network Layers",
      layers: {
        CRD_Model_Road_Network_v1: {
          label: "CRD Model Road Network v1",
          description: "Show/hide CRD road network on the map",
          fileName: "CRD_Model_Road_Network_v1.geojson",
          mandatory: false,
        },
        CRD_Transit_Routes_v1: {
          label: "CRD Transit Routes v1",
          description: "Show/hide CRD transit routes on the map",
          fileName: "CRD_Transit_Routes_v1.geojson",
          mandatory: false,
        },
      },
    },
    zones: {
      title: "Zone Layers",
      layers: {
        CRD_Municipal_Boundaries_v1: {
          label: "Municipal Boundaries",
          description: "Show/hide municipal boundaries on the map",
          fileName: "CRD_Municipal_Boundaries_v1.geojson",
          mandatory: false,
        },
        CRD_TAZ_2025_v2: {
          label: "TAZ",
          description: "Show/hide TAZ boundaries on the map",
          fileName: "CRD_TAZ_2025_v2.geojson",
          mandatory: true,
        },
      },
    },
  };
  const pendingColumns = [
    {
      name: "TAZ",
      selector: (row) => row.TAZ,
      sortable: true,
      width: "10%",
    },
    {
      name: "Version",
      selector: (row) => row.version_number,
      sortable: true,
      width: "10%",
    },
    { name: "Created By", selector: (row) => row.created_by, sortable: true },
    {
      name: "Created At",
      selector: (row) => new Date(row.created_at).toLocaleString(),
      sortable: true,
    },
    {
      name: "Actions",
      width: "10%",
      cell: (row) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleSelectFeature(row.TAZ)}
          title="View Feature"
        >
          <i className="fa fa-eye"></i>
        </button>
      ),
    },
  ];

  // ==================== LOCAL STATES ====================
  const context = React.useContext(AppSettings);
  const { user } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const isAdmin =
    user?.role_name === "Management" || user?.role_name === "Super Admin" || user?.role_name === "CRD Management";

  // ==================== ZUSTAND STORE ====================
  const setStaticLayer = useModelMappingStore((state) => state.setStaticLayer);
  const setLayerVisibility = useModelMappingStore(
    (state) => state.setLayerVisibility
  );
  const visibleLayers = useModelMappingStore((state) => state.visibleLayers);
  const setSelectedFeature = useModelMappingStore(
    (state) => state.setSelectedFeature
  );
  const clearSelectedFeature = useModelMappingStore(
    (state) => state.clearSelectedFeature
  );

  // ==================== REACT QUERY HOOKS ====================
  const { data: availableLayers, isLoading: isLoadingLayers, isError: isLayersError, isFetching: isFetchingLayers } =
    useAvailableLayers();
  const { data: pendingData, isLoading: isLoadingPending , isFetching: isFetchingPending } = usePendingFeatures(
    user?.role_name === "Management" || user?.role_name === "Super Admin" || user?.role_name === "CRD Management"
      ? "CRD_TAZ_2025_v2.geojson"
      : null,
    { 
      // Only fetch pending features if:
      // 1. Layers finished loading (!isLoadingLayers)
      // 2. Layers loaded successfully (!isLayersError - no 404)
      // 3. Layers not currently refetching (!isFetchingLayers)
      // 4. Layers data exists (!!availableLayers - prevents race condition on invalidation)
      enabled: !isLoadingLayers && !isLayersError && !isFetchingLayers && !!availableLayers 
    }
  );

  // ==================== HANDLERS ====================
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    // Check if mandatory
    let isMandatory = false;
    Object.values(layerCategories).forEach((category) => {
      if (category.layers[name]?.mandatory) {
        isMandatory = true;
      }
    });

    if (isMandatory && !checked) {
      console.log(`❌ Cannot uncheck mandatory layer: ${name}`);
      return;
    }

    // Update layer visibility in Zustand
    setIsProcessing(true);

    setTimeout(() => {
      setLayerVisibility(name, checked);
      setIsProcessing(false);
      console.log(`✅ Layer visibility updated: ${name} = ${checked}`);
    }, 100);
  };

  const handleSelectFeature = (TAZ) => {
    console.log(`🔍 Selecting feature TAZ: ${TAZ}`);

    // Get store state
    const store = useModelMappingStore.getState();
    const { mapInstance, layerGroupsRef } = store;
    
    // Clear previous selection
    clearSelectedFeature();

    // Deselect all layers on map and find the matching layer
    if (mapInstance && layerGroupsRef) {
      let targetLayer = null;

      mapInstance.eachLayer((mapLayer) => {
        // Deselect all layers
        if (mapLayer._isSelected) {
          mapLayer._isSelected = false;
          if (typeof mapLayer._getOriginalStyle === "function") {
            mapLayer.setStyle(mapLayer._getOriginalStyle());
          }
        }

        // Find the target layer by TAZ
        if (mapLayer.feature?.properties?.TAZ == TAZ) {
          targetLayer = mapLayer;
        }
      });

      // Select the target layer
      if (targetLayer) {
        targetLayer._isSelected = true;
        const originalStyle = targetLayer._getOriginalStyle();

        // Use the same selection style from handleTAZClick
        const { getSelectionStyle } = require("../map/utils/map-styles.js");
        targetLayer.setStyle(getSelectionStyle("area", originalStyle));

        console.log("📌 TAZ layer selected programmatically:", TAZ);
      } else {
        console.warn("⚠️ Could not find layer for TAZ:", TAZ);
      }
    }

    // Set new selection (triggers sidebar and details component)
    setSelectedFeature({
      TAZ: TAZ,
      _layerFilename: "CRD_TAZ_2025_v2.geojson",
      _timestamp: Date.now(),
    });

    // Open sidebar
    context.setAppSidebarEndToggled(true);

    // Close modal
    setShowPendingModal(false);
  };

  // ==================== LOCAL HOOKS ====================
  // Bootstrap popovers
  useEffect(() => {
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );
    if (popoverTriggerList) {
      popoverTriggerList.map(function (elm) {
        new bootstrap.Popover(elm);
        return true;
      });
    }
  }, []);

  useEffect(() => {
    console.log("🌐 ModelMappingSelector: useEffect triggered for layer sync");
    if (!availableLayers || isLoadingLayers) {
      console.log("🌐 ModelMappingSelector: Layers not ready, skipping sync");
      return;
    }

    if (Object.keys(availableLayers).length === 0) {
      console.log("🌐 ModelMappingSelector: No layers available");
      return;
    }

    console.log(
      "🌐 ModelMappingSelector: Syncing static layers with selectors"
    );

    // Filter layers - only keep those in selectors array
    const filteredLayers = {};
    Object.entries(availableLayers).forEach(([key, layer]) => {
      if (selectors.includes(key)) {
        filteredLayers[key] = layer;
      }
    });

    console.log(
      "📦 Filtered layers (in selectors):",
      Object.keys(filteredLayers)
    );

    // Store filtered layers in Zustand
    Object.entries(filteredLayers).forEach(([key, processedLayer]) => {
      setStaticLayer(key, processedLayer);
    });

    // Initialize visibility based on layerCategories (mandatory = true, others = false)
    if (Object.keys(visibleLayers).length === 0) {
      console.log("🔧 Initializing layer visibility from layerCategories");

      const newVisibleLayers = {};
      Object.values(layerCategories).forEach((category) => {
        Object.entries(category.layers).forEach(([key, definition]) => {
          // Only set visibility for layers that exist in selectors
          if (selectors.includes(key)) {
            newVisibleLayers[key] = definition.mandatory || false;
          }
        });
      });

      console.log("✅ Initial visibility state:", newVisibleLayers);

      // Batch update all visibilities at once
      Object.entries(newVisibleLayers).forEach(([key, isVisible]) => {
        setLayerVisibility(key, isVisible);
      });
    }

    console.log("✅ All layers loaded into Zustand");
  }, [availableLayers, visibleLayers, setStaticLayer, setLayerVisibility]);

  // Pending features for table
  const pendingFeatures = React.useMemo(() => {
    if (!pendingData?.features) return [];

    return pendingData.features.map((feature) => ({
      TAZ: feature.TAZ,
      version_number: feature.pending_versions?.[0]?.version || "N/A",
      created_by: feature.pending_versions?.[0]?.created_by || "N/A",
      created_at: feature.pending_versions?.[0]?.created_at || "N/A",
    }));
  }, [pendingData]);

  // ==================== RENDER ====================
  return (
    <div className="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
      <div className="card-body">
        {/* Header */}
        <div className="row">
          <div className="col-lg-12">
            <h4 className="mb-3 text-gray-500">
              <b>MODEL MAPPINGS CONTROLS</b>
              <span className="ms-2">
                <i
                  className="fa fa-info-circle"
                  data-bs-toggle="popover"
                  data-bs-trigger="hover"
                  data-bs-title="Model Mappings Controls"
                  data-bs-placement="top"
                  data-bs-content="Select the layers you want to display on the map. Changes are applied automatically."
                ></i>
              </span>
            </h4>

            {/* Loading indicator */}
            {(isLoadingLayers || isLoadingPending || isFetchingLayers || isFetchingPending) && (
              <div className="d-flex align-items-center text-warning mb-3">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <small>Loading available layers...</small>
              </div>
            )}

            {/* Processing indicator */}
            {isProcessing && (
              <div className="d-flex align-items-center text-primary mb-3">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <small>Updating map...</small>
              </div>
            )}
          </div>
        </div>

        {/* Layer Controls */}
        {!isLoadingLayers && (
          <div className="row">
            <div className="col-lg-12">
              {/* Points Layers Section */}
              <h5 className="text-white mb-0">
                {layerCategories.points.title}
              </h5>
              <div className="accordion mb-0" id="accordionPoints">
                {Object.entries(layerCategories.points.layers).map(
                  ([key, definition], index) => {
                    const style = pointStyles[key];
                    const iconColor = style?.fillColor || "#6366f1";
                    return (
                      selectors.includes(key) && (
                        <div
                          key={key}
                          className="accordion-item bg-gray-900 border-0"
                        >
                          <div
                            className="accordion-header"
                            id={`headingPoints${index}`}
                          >
                            <button
                              className={`accordion-button py-1 bg-gray-800 text-white ${
                                !visibleLayers[key] ? "collapsed" : ""
                              }`}
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapsePoints${index}`}
                            >
                              <i
                                className="fa fa-circle fa-fw me-2 fs-16px"
                                style={{ color: iconColor }}
                              ></i>
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  id={key}
                                  name={key}
                                  checked={visibleLayers[key] || false}
                                  onChange={handleCheckboxChange}
                                  className="form-check-input"
                                  disabled={
                                    definition.mandatory || isProcessing
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <label
                                  htmlFor={key}
                                  className="form-check-label ms-2"
                                >
                                  {definition.label}
                                  {definition.mandatory && (
                                    <span className="text-warning ms-1">
                                      (Required)
                                    </span>
                                  )}
                                </label>
                              </div>
                            </button>
                          </div>
                          <div
                            id={`collapsePoints${index}`}
                            className={`accordion-collapse collapse ${
                              visibleLayers[key] ? "show" : ""
                            }`}
                            data-bs-parent="#accordionPoints"
                          >
                            <div className="accordion-body bg-gray-900 text-white">
                              <small className="text-muted">
                                Layer: {key}
                                <br />
                                Type: Point Layer
                                {definition.mandatory && (
                                  <>
                                    <br />
                                    Status: Mandatory - Always visible
                                  </>
                                )}
                              </small>
                            </div>
                          </div>
                        </div>
                      )
                    );
                  }
                )}
              </div>

              {/* Network Layers Section */}
              <h5 className="text-white mb-0 mt-3">
                {layerCategories.network.title}
              </h5>
              <div className="accordion mb-0" id="accordionNetwork">
                {Object.entries(layerCategories.network.layers).map(
                  ([key, definition], index) => {
                    const style = lineStyles[key];
                    const iconColor = style?.color || "#6366f1";
                    return (
                      selectors.includes(key) && (
                        <div
                          key={key}
                          className="accordion-item bg-gray-900 border-0"
                        >
                          <div
                            className="accordion-header"
                            id={`headingNetwork${index}`}
                          >
                            <button
                              className={`accordion-button py-1 bg-gray-800 text-white ${
                                !visibleLayers[key] ? "collapsed" : ""
                              }`}
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapseNetwork${index}`}
                            >
                              <i
                                className="fa fa-solid fa-route me-2 fs-16px"
                                style={{ color: iconColor }}
                              ></i>
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  id={key}
                                  name={key}
                                  checked={visibleLayers[key] || false}
                                  onChange={handleCheckboxChange}
                                  className="form-check-input"
                                  disabled={
                                    definition.mandatory || isProcessing
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <label
                                  htmlFor={key}
                                  className="form-check-label ms-2"
                                >
                                  {definition.label}
                                  {definition.mandatory && (
                                    <span className="text-warning ms-1">
                                      (Required)
                                    </span>
                                  )}
                                </label>
                              </div>
                            </button>
                          </div>
                          <div
                            id={`collapseNetwork${index}`}
                            className={`accordion-collapse collapse ${
                              visibleLayers[key] ? "show" : ""
                            }`}
                            data-bs-parent="#accordionNetwork"
                          >
                            <div className="accordion-body bg-gray-900 text-white">
                              <small className="text-muted">
                                Layer: {key}
                                <br />
                                Type: Network Layer
                                {definition.mandatory && (
                                  <>
                                    <br />
                                    Status: Mandatory - Always visible
                                  </>
                                )}
                              </small>
                            </div>
                          </div>
                        </div>
                      )
                    );
                  }
                )}
              </div>

              {/* Zone Layers Section */}
              <h5 className="text-white mb-0 mt-3">
                {layerCategories.zones.title}
              </h5>
              <div className="accordion mb-0" id="accordionZones">
                {Object.entries(layerCategories.zones.layers).map(
                  ([key, definition], index) => {
                    const style = areaStyles[key];
                    const iconColor = style?.fillColor || "#6366f1";
                    return (
                      selectors.includes(key) && (
                        <div
                          key={key}
                          className="accordion-item bg-gray-900 border-0"
                        >
                          <div
                            className="accordion-header"
                            id={`headingZones${index}`}
                          >
                            <button
                              className={`accordion-button py-1 bg-gray-800 text-white ${
                                !visibleLayers[key] ? "collapsed" : ""
                              }`}
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapseZones${index}`}
                            >
                              <i
                                className="fa-solid fa-layer-group fa-fw me-2 fs-16px"
                                style={{ color: iconColor }}
                              ></i>
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  id={key}
                                  name={key}
                                  checked={visibleLayers[key] || false}
                                  onChange={handleCheckboxChange}
                                  className="form-check-input"
                                  disabled={
                                    definition.mandatory || isProcessing
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <label
                                  htmlFor={key}
                                  className="form-check-label ms-2"
                                >
                                  {definition.label}
                                  {definition.mandatory && (
                                    <span className="text-warning ms-1">
                                      (Required)
                                    </span>
                                  )}
                                </label>
                              </div>
                            </button>
                          </div>
                          <div
                            id={`collapseZones${index}`}
                            className={`accordion-collapse collapse ${
                              visibleLayers[key] ? "show" : ""
                            }`}
                            data-bs-parent="#accordionZones"
                          >
                            <div className="accordion-body bg-gray-900 text-white">
                              <small className="text-muted">
                                Layer: {key}
                                <br />
                                Type: Zone Layer
                                {definition.mandatory && (
                                  <>
                                    <br />
                                    Status: Mandatory - Always visible
                                  </>
                                )}
                              </small>
                            </div>
                          </div>
                        </div>
                      )
                    );
                  }
                )}
              </div>
            </div>
          </div>
        )}

        {/* Active Layers Badge Display */}
        {!isLoadingLayers && (
          <div className="row mt-3">
            <div className="col-lg-12">
              <h5 className="text-gray-500">Active Map Layers</h5>
              <div className="mt-2 d-flex flex-row flex-wrap gap-1">
                {Object.entries(layerCategories).map(
                  ([categoryKey, category]) =>
                    Object.entries(category.layers).map(
                      ([key, definition]) =>
                        visibleLayers[key] && (
                          <span
                            key={key}
                            className={`badge fw-bold fs-12px ${
                              definition.mandatory
                                ? "bg-warning bg-opacity-15 text-warning"
                                : "bg-primary bg-opacity-15 text-primary"
                            }`}
                          >
                            {definition.label}
                            {definition.mandatory && " (Required)"}
                          </span>
                        )
                    )
                )}
                {Object.entries(visibleLayers).every(
                  ([key, value]) => !value
                ) && (
                  <span className="text-muted text-gray-500">
                    No layers selected
                  </span>
                )}
              </div>

              {/* Pending features button (admin only) */}
              {isAdmin && (
                <div className="mt-3 text-center">
                  <button
                    className="btn btn-success btn-lg w-100 mt-auto"
                    onClick={() => setShowPendingModal(true)}
                  >
                    See Pending Feature Versions ({pendingFeatures.length})
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pending Versions Modal */}
        <Modal
          show={showPendingModal}
          onHide={() => setShowPendingModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Pending Feature Versions ({pendingFeatures.length})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <DataTable
              columns={pendingColumns}
              data={pendingFeatures}
              pagination
              highlightOnHover
              responsive
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowPendingModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default ModelMappingSelector;
